import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { FileText, Download, Trash2, Lock, LockOpen, Share2, Copy, Hash, FolderOpen, Plus, Eye } from 'lucide-react';
import bcrypt from 'bcryptjs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { decryptFile } from '../lib/encryption';
import { truncate } from '../lib/validation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const SELECTED_FOLDER_STORAGE_KEY = 'securevault_selected_folder_id';
const IMAGE_PREVIEW_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const TEXT_PREVIEW_EXTENSIONS = ['txt', 'md', 'csv'];

const getFileExtension = (fileName = '') => {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const getPreviewType = (fileName) => {
  const extension = getFileExtension(fileName);
  if (IMAGE_PREVIEW_EXTENSIONS.includes(extension)) return 'image';
  if (extension === 'pdf') return 'pdf';
  if (TEXT_PREVIEW_EXTENSIONS.includes(extension)) return 'text';
  return 'unsupported';
};

const isPreviewable = () => true;

const FilesPage = () => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [shareDownloadCounts, setShareDownloadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareExpiry, setShareExpiry] = useState('24');
  const [sharePw, setSharePw] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [pwPrompt, setPwPrompt] = useState(null);
  const [pwPromptAction, setPwPromptAction] = useState('download');
  const [enteredPw, setEnteredPw] = useState('');
  const [previewing, setPreviewing] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewType, setPreviewType] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(sessionStorage.getItem(SELECTED_FOLDER_STORAGE_KEY) || '');
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const { user, logAction } = useAuth();

  useEffect(() => {
    fetchFiles();
    fetchFolders();
  }, [user]);

  const fetchFiles = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('files')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFiles(data || []);
      await fetchShareDownloadCounts(data || []);
    } catch (error) {
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  const fetchShareDownloadCounts = async (fileRows) => {
    const sharedFiles = fileRows.filter((file) => file.share_token);

    if (sharedFiles.length === 0) {
      setShareDownloadCounts({});
      return;
    }

    const countEntries = await Promise.all(
      sharedFiles.map(async (file) => {
        const { count, error } = await supabase
          .from('share_downloads')
          .select('*', { count: 'exact', head: true })
          .eq('file_id', file.id);

        if (error) {
          console.warn('Failed to fetch share download count:', error);
          return [file.id, 0];
        }

        return [file.id, count || 0];
      })
    );

    setShareDownloadCounts(Object.fromEntries(countEntries));
  };

  const fetchFolders = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFolders(data || []);
    } catch (error) {
      toast.error('Failed to fetch folders');
    }
  };

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId);
    setSelectedFiles(new Set());
    if (folderId) {
      sessionStorage.setItem(SELECTED_FOLDER_STORAGE_KEY, folderId);
    } else {
      sessionStorage.removeItem(SELECTED_FOLDER_STORAGE_KEY);
    }
  };

  const handleCreateFolder = async () => {
    const folderName = newFolderName.trim().slice(0, 100);
    if (!folderName || !user) return;

    setCreatingFolder(true);
    try {
      const { error } = await supabase
        .from('folders')
        .insert({ user_id: user.id, name: folderName });

      if (error) throw error;
      setNewFolderName('');
      setFolderDialogOpen(false);
      await fetchFolders();
      toast.success('Folder created');
    } catch (error) {
      toast.error(error.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    const { error } = await supabase
      .from('folders')
      .delete()
      .eq('id', folder.id);

    if (error) {
      toast.error('Failed to delete folder');
      return;
    }

    if (selectedFolderId === folder.id) handleSelectFolder('');
    setFolders(folders.filter((item) => item.id !== folder.id));
    setFiles(files.map((file) => file.folder_id === folder.id ? { ...file, folder_id: null } : file));
    toast.success('Folder deleted');
  };

  const handleMoveToFolder = async (fileRecord, folderId) => {
    const { error } = await supabase
      .from('files')
      .update({ folder_id: folderId || null })
      .eq('id', fileRecord.id);

    if (error) {
      toast.error('Failed to move file');
      return;
    }

    setFiles(files.map((file) => file.id === fileRecord.id ? { ...file, folder_id: folderId || null } : file));
    toast.success(folderId ? 'File moved to folder' : 'File moved to All Files');
  };

  const handleToggleFileSelection = (fileId) => {
    setSelectedFiles((current) => {
      const next = new Set(current);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleToggleSelectAllVisible = (visibleFiles) => {
    const visibleIds = visibleFiles.map((file) => file.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedFiles.has(id));

    setSelectedFiles((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const filesToDelete = displayedFiles.filter((file) => selectedFiles.has(file.id));
    if (filesToDelete.length === 0) return;

    setBulkDeleting(true);
    try {
      for (const file of filesToDelete) {
        const { error: storageError } = await supabase.storage
          .from('vault-files')
          .remove([file.storage_path]);

        if (storageError) console.warn('Storage delete warning:', storageError);

        const { error: dbError } = await supabase
          .from('files')
          .delete()
          .eq('id', file.id);

        if (dbError) throw dbError;
      }

      toast.success(`${filesToDelete.length} file${filesToDelete.length !== 1 ? 's' : ''} deleted`);
      setSelectedFiles(new Set());
      setBulkDeleteOpen(false);
      await fetchFiles();
    } catch (error) {
      toast.error('Failed to delete selected files');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDownload = async (fileRecord, password) => {
    // Check download password
    if (fileRecord.download_password && !password) {
      setPwPrompt(fileRecord);
      setPwPromptAction('download');
      return;
    }
    if (fileRecord.download_password && !bcrypt.compareSync(password, fileRecord.download_password)) {
      toast.error('Incorrect download password');
      return;
    }
    setPwPrompt(null);
    setEnteredPw('');
    setDownloading(fileRecord.id);
    try {
      const { data, error } = await supabase.storage.from('vault-files').download(fileRecord.storage_path);
      if (error) throw error;

      let blob;
      if (fileRecord.encryption_iv) {
        const encrypted = await data.arrayBuffer();
        const decrypted = await decryptFile(encrypted, fileRecord.encryption_iv);
        blob = new Blob([decrypted]);
      } else {
        blob = data;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileRecord.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      // Suspicious activity: detect rapid downloads
      await logAction(`Downloaded file: ${fileRecord.file_name}`);
      toast.success('File downloaded' + (fileRecord.encryption_iv ? ' and decrypted!' : '!'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    } finally {
      setDownloading(null);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewFile(null);
    setPreviewUrl('');
    setPreviewText('');
    setPreviewType(null);
  };

  const handlePreview = async (fileRecord, password) => {
    if (fileRecord.download_password && !password) {
      setPwPrompt(fileRecord);
      setPwPromptAction('preview');
      return;
    }
    if (fileRecord.download_password && !bcrypt.compareSync(password, fileRecord.download_password)) {
      toast.error('Incorrect download password');
      return;
    }

    setPwPrompt(null);
    setPwPromptAction('download');
    setEnteredPw('');
    setPreviewing(fileRecord.id);

    try {
      const type = getPreviewType(fileRecord.file_name);

      if (type === 'unsupported') {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewFile(fileRecord);
        setPreviewUrl('');
        setPreviewText('');
        setPreviewType(type);
        return;
      }

      const { data, error } = await supabase.storage.from('vault-files').download(fileRecord.storage_path);
      if (error) throw error;

      let blob;
      if (fileRecord.encryption_iv) {
        const encrypted = await data.arrayBuffer();
        const decrypted = await decryptFile(encrypted, fileRecord.encryption_iv);
        blob = new Blob([decrypted], { type: data.type || 'application/octet-stream' });
      } else {
        blob = data;
      }

      const objectUrl = URL.createObjectURL(blob);

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewFile(fileRecord);
      setPreviewUrl(objectUrl);
      setPreviewType(type);

      if (type === 'text') {
        setPreviewText(await blob.text());
      } else {
        setPreviewText('');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview file');
    } finally {
      setPreviewing(null);
    }
  };

  const handleShare = async () => {
    if (!shareTarget) return;
    const token = crypto.randomUUID();
    const expiry = new Date(Date.now() + parseInt(shareExpiry) * 3600000).toISOString();
    const trimmedSharePassword = truncate(sharePw, 100);
    const hashedSharePassword = trimmedSharePassword
      ? bcrypt.hashSync(trimmedSharePassword, 10)
      : shareTarget.download_password || null;
    const { error } = await supabase.from('files').update({
      share_token: token,
      share_expiry: expiry,
      share_password: hashedSharePassword,
    }).eq('id', shareTarget.id);
    if (error) { toast.error('Failed to create share link'); return; }
    const link = `${window.location.origin}/shared/${token}`;
    setShareLink(link);
    await logAction(`Shared file: ${shareTarget.file_name}`);
    toast.success('Share link created!');
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied!');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('vault-files')
        .remove([deleteTarget.storage_path]);

      if (storageError) console.warn('Storage delete warning:', storageError);

      // Delete from database
      const { error: dbError } = await supabase
        .from('files')
        .delete()
        .eq('id', deleteTarget.id);

      if (dbError) throw dbError;

      setFiles(files.filter((f) => f.id !== deleteTarget.id));
      await logAction(`Deleted file: ${deleteTarget.file_name}`);
      toast.success('File deleted successfully');
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const selectedFolder = folders.find((folder) => folder.id === selectedFolderId);
  const folderFilteredFiles = selectedFolderId
    ? files.filter((file) => file.folder_id === selectedFolderId)
    : files;
  const searchedFiles = folderFilteredFiles.filter((file) =>
    file.file_name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );
  const displayedFiles = [...searchedFiles].sort((a, b) => {
    switch (sortOrder) {
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'name-asc':
        return a.file_name.localeCompare(b.file_name);
      case 'name-desc':
        return b.file_name.localeCompare(a.file_name);
      case 'largest':
        return (b.file_size || 0) - (a.file_size || 0);
      case 'smallest':
        return (a.file_size || 0) - (b.file_size || 0);
      case 'newest':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });
  const visibleSelectedFiles = displayedFiles.filter((file) => selectedFiles.has(file.id));
  const allVisibleSelected = displayedFiles.length > 0 && displayedFiles.every((file) => selectedFiles.has(file.id));
  const hasFilesInCurrentFolder = folderFilteredFiles.length > 0;
  const noSearchResults = hasFilesInCurrentFolder && displayedFiles.length === 0;

  const renderFileTableSkeleton = () => (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="files-loading-skeleton">
      <table className="w-full table-fixed">
        <tbody className="divide-y divide-border">
          {Array.from({ length: 5 }).map((_, index) => (
            <tr key={index}>
              <td className="w-10 px-3 sm:px-4 py-4">
                <div className="w-4 h-4 rounded bg-muted animate-pulse" />
              </td>
              <td className="px-3 sm:px-4 py-4">
                <div className="w-48 max-w-full h-4 rounded bg-muted animate-pulse" />
              </td>
              <td className="hidden sm:table-cell w-24 px-3 sm:px-4 py-4">
                <div className="w-16 h-4 rounded bg-muted animate-pulse" />
              </td>
              <td className="hidden sm:table-cell w-32 px-3 sm:px-4 py-4">
                <div className="w-20 h-6 rounded-full bg-muted animate-pulse" />
              </td>
              <td className="hidden md:table-cell w-28 px-3 sm:px-4 py-4">
                <div className="w-24 h-4 rounded bg-muted animate-pulse" />
              </td>
              <td className="w-[136px] sm:w-[260px] md:w-[360px] px-3 sm:px-4 py-4">
                <div className="flex flex-wrap justify-end gap-2">
                  <div className="w-20 h-8 rounded bg-muted animate-pulse" />
                  <div className="w-20 h-8 rounded bg-muted animate-pulse" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="files-title">
              My Files
            </h1>
            <p className="text-base text-muted-foreground mt-1">{displayedFiles.length} file{displayedFiles.length !== 1 ? 's' : ''} in vault</p>
          </div>
          <Button onClick={() => setFolderDialogOpen(true)} className="w-full sm:w-auto" data-testid="new-folder-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <button
            onClick={() => handleSelectFolder('')}
            className={`${selectedFolderId ? 'hover:text-foreground' : 'text-foreground font-medium'} transition-colors duration-150`}
            data-testid="all-files-breadcrumb"
          >
            All Files
          </button>
          {selectedFolder && (
            <>
              <span>&gt;</span>
              <span className="text-foreground font-medium">{selectedFolder.name}</span>
            </>
          )}
        </div>

        {loading ? (
          renderFileTableSkeleton()
        ) : folders.length === 0 && !hasFilesInCurrentFolder ? (
          <div className="text-center py-12 bg-card border border-border rounded-lg" data-testid="no-files">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No files uploaded yet</h3>
            <p className="text-sm text-muted-foreground">Upload your first file to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {!selectedFolderId && folders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="folders-list">
                  {folders.map((folder) => (
                    <div key={folder.id} className="bg-card border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors duration-150">
                      <button
                        onClick={() => handleSelectFolder(folder.id)}
                        className="flex w-full items-start gap-3 text-left min-w-0"
                        data-testid="folder-row"
                      >
                        <FolderOpen className="w-5 h-5 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{folder.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            Created {new Date(folder.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteFolder(folder)}
                        className="mt-3 w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid="delete-folder-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />Delete
                      </Button>
                    </div>
                  ))}
              </div>
            )}

            {!hasFilesInCurrentFolder ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg" data-testid="no-files-in-folder">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No files here yet</h3>
                <p className="text-sm text-muted-foreground">Move files into this folder or upload a new file.</p>
              </div>
            ) : (
              <>
                {visibleSelectedFiles.length > 0 && (
                  <div className="bg-muted border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="bulk-actions-toolbar">
                    <p className="text-sm font-medium">
                      {visibleSelectedFiles.length} file{visibleSelectedFiles.length !== 1 ? 's' : ''} selected
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setBulkDeleteOpen(true)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid="bulk-delete-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Selected
                      </Button>
                      <Button variant="ghost" onClick={() => setSelectedFiles(new Set())} data-testid="bulk-deselect-btn">
                        Deselect All
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3" data-testid="files-search-sort">
                  <Input
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedFiles(new Set());
                    }}
                    placeholder="Search files..."
                    className="w-full"
                    data-testid="files-search-input"
                  />
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="h-10 w-full sm:w-48 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="files-sort-select"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="largest">Largest first</option>
                    <option value="smallest">Smallest first</option>
                  </select>
                </div>

                {noSearchResults ? (
                  <div className="text-center py-12 bg-card border border-border rounded-lg" data-testid="no-search-results">
                    <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No files match your search</h3>
                    <p className="text-sm text-muted-foreground">Try another file name or clear the search field.</p>
                  </div>
                ) : (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div>
                    <table className="w-full table-fixed">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="w-10 px-3 sm:px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              onChange={() => handleToggleSelectAllVisible(displayedFiles)}
                              aria-label="Select all visible files"
                              data-testid="select-all-files-checkbox"
                              className="h-4 w-4 rounded border-border"
                            />
                          </th>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">File Name</th>
                          <th className="hidden sm:table-cell w-24 px-3 sm:px-4 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Size</th>
                          <th className="hidden sm:table-cell w-32 px-3 sm:px-4 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Status</th>
                          <th className="hidden md:table-cell w-28 px-3 sm:px-4 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Uploaded</th>
                          <th className="w-[136px] sm:w-[260px] md:w-[360px] px-3 sm:px-4 py-3 text-right text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {displayedFiles.map((file) => (
                    <tr key={file.id} className="hover:bg-muted/30 transition-colors duration-150" data-testid="file-row">
                      <td className="px-3 sm:px-4 py-4 align-top">
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(file.id)}
                          onChange={() => handleToggleFileSelection(file.id)}
                          aria-label={`Select ${file.file_name}`}
                          data-testid="file-select-checkbox"
                          className="h-4 w-4 rounded border-border"
                        />
                      </td>
                      <td className="px-3 sm:px-4 py-4 align-top min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-5 h-5 text-primary shrink-0" />
                          <span className="font-medium text-sm truncate min-w-0" title={file.file_name}>{file.file_name}</span>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-4 py-4 align-top text-sm font-mono text-muted-foreground">{formatBytes(file.file_size)}</td>
                      <td className="hidden sm:table-cell px-3 sm:px-4 py-4 align-top">
                        {file.encryption_iv ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">
                            <Lock className="w-3 h-3" /> Encrypted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                            <LockOpen className="w-3 h-3" /> Unencrypted
                          </span>
                        )}
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-4 py-4 align-top text-sm font-mono text-muted-foreground">
                        {new Date(file.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 sm:px-4 py-4 align-top text-right">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {file.file_hash && (
                            <Button size="sm" variant="ghost" title={`SHA-256: ${file.file_hash}`} onClick={() => { navigator.clipboard.writeText(file.file_hash); toast.success('Hash copied!'); }} data-testid="copy-hash-btn">
                              <Hash className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => { setShareTarget(file); setShareLink(''); setSharePw(''); setShareExpiry('24'); }} data-testid="share-file-btn">
                            <Share2 className="w-4 h-4 mr-1" />Share
                          </Button>
                          {file.share_token && (
                            <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary" data-testid="share-download-count">
                              {shareDownloadCounts[file.id] || 0} download{(shareDownloadCounts[file.id] || 0) === 1 ? '' : 's'}
                            </span>
                          )}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button size="sm" variant="outline" data-testid="move-file-btn">
                                <FolderOpen className="w-4 h-4 mr-1" />Move
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-56">
                              <div className="space-y-2">
                                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Move to folder</p>
                                <button
                                  onClick={() => handleMoveToFolder(file, '')}
                                  className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted"
                                >
                                  All Files
                                </button>
                                {folders.map((folder) => (
                                  <button
                                    key={folder.id}
                                    onClick={() => handleMoveToFolder(file, folder.id)}
                                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted"
                                  >
                                    {folder.name}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          {isPreviewable(file.file_name) && (
                            <Button size="sm" variant="outline" onClick={() => handlePreview(file)} disabled={previewing === file.id} data-testid="preview-file-btn">
                              <Eye className="w-4 h-4 mr-1" />{previewing === file.id ? '...' : 'Preview'}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleDownload(file)} disabled={downloading === file.id} data-testid="download-file-btn">
                            <Download className="w-4 h-4 mr-1" />{downloading === file.id ? '...' : 'Download'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDeleteTarget(file)} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="delete-file-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* New Folder Dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Folder</DialogTitle>
            <DialogDescription>Create a folder to organize your files.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value.slice(0, 100))}
              maxLength={100}
              placeholder="Project documents"
              className="mt-2"
              data-testid="new-folder-name-input"
            />
          </div>
          <DialogFooter>
            <Button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()} data-testid="create-folder-btn">
              {creatingFolder ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.file_name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {visibleSelectedFiles.length} file{visibleSelectedFiles.length !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <Dialog open={!!shareTarget} onOpenChange={() => setShareTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share File</DialogTitle>
            <DialogDescription>Create a time-limited share link for <strong>{shareTarget?.file_name}</strong></DialogDescription>
          </DialogHeader>
          {!shareLink ? (
            <div className="space-y-4 py-2">
              <div>
                <Label>Expiry</Label>
                <select value={shareExpiry} onChange={e => setShareExpiry(e.target.value)} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="share-expiry-select">
                  <option value="1">1 hour</option>
                  <option value="24">24 hours</option>
                  <option value="72">3 days</option>
                  <option value="168">7 days</option>
                </select>
              </div>
              <div>
                <Label>Password (optional)</Label>
                <Input type="password" placeholder="Leave empty for no password" value={sharePw} onChange={e => setSharePw(e.target.value.slice(0, 100))} maxLength={100} data-testid="share-password-input" className="mt-1" />
              </div>
              {shareTarget?.download_password && (
                <div className="rounded-md border border-primary/30 bg-primary/10 p-3 text-xs text-primary" data-testid="share-download-password-notice">
                  This file has a download password. The share link will require the same password unless you set a different one.
                </div>
              )}
              <Button onClick={handleShare} className="w-full" data-testid="create-share-btn">Create Share Link</Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="flex gap-2">
                <Input value={shareLink} readOnly className="font-mono text-xs" data-testid="share-link-input" />
                <Button onClick={copyShareLink} variant="outline" data-testid="copy-share-btn"><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Expires in {shareExpiry} hour{shareExpiry !== '1' ? 's' : ''}{(sharePw || shareTarget?.download_password) ? ' (password protected)' : ''}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{previewFile?.file_name}</DialogTitle>
            <DialogDescription>Preview file content without downloading.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {previewType === 'image' && previewUrl && (
              <img
                src={previewUrl}
                alt={previewFile?.file_name || 'File preview'}
                className="max-h-[70vh] w-full object-contain rounded-md border border-border bg-muted"
                data-testid="image-preview"
              />
            )}
            {previewType === 'pdf' && previewUrl && (
              <iframe
                src={previewUrl}
                title={previewFile?.file_name || 'PDF preview'}
                className="h-[600px] w-full rounded-md border border-border bg-muted"
                data-testid="pdf-preview"
              />
            )}
            {previewType === 'text' && (
              <pre className="max-h-[500px] overflow-auto rounded-md border border-border bg-muted p-4 text-left text-sm font-mono whitespace-pre-wrap" data-testid="text-preview">
                {previewText}
              </pre>
            )}
            {previewType === 'unsupported' && (
              <div className="rounded-md border border-border bg-muted p-6 text-sm text-muted-foreground" data-testid="unsupported-preview">
                <p className="font-medium text-foreground mb-2">Preview not available for this file type.</p>
                <p>Supported preview formats: Images, PDFs, and text files.</p>
                <p>You can download the file to view it.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => handleDownload(previewFile)} disabled={!previewFile || downloading === previewFile?.id} data-testid="preview-download-btn">
              <Download className="w-4 h-4 mr-2" />
              {downloading === previewFile?.id ? 'Downloading...' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Password Prompt */}
      <Dialog open={!!pwPrompt} onOpenChange={() => { setPwPrompt(null); setPwPromptAction('download'); setEnteredPw(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Required</DialogTitle>
            <DialogDescription>This file is password-protected. Enter the download password.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input type="password" placeholder="Enter password" value={enteredPw} onChange={e => setEnteredPw(e.target.value)} data-testid="download-pw-prompt-input" />
          </div>
          <DialogFooter>
            <Button
              onClick={() => pwPromptAction === 'preview' ? handlePreview(pwPrompt, enteredPw) : handleDownload(pwPrompt, enteredPw)}
              data-testid="download-pw-submit-btn"
            >
              {pwPromptAction === 'preview' ? 'Preview' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default FilesPage;
